import React, { useState, Fragment, useEffect, Suspense, useRef } from 'react';
import { PermissionContainerStyled, FooterButtonStyled, PermissionFooterStyled, FreezeCellStyled } from './styled';
import {
  Box,
  MultiSelectCheckbox,
  TextFieldSize,
  TextFieldAppearance,
  Text,
  ButtonAppearance,
  useToastsDispatch,
  Spinner,
  SpinnerSize,
} from '@bechtel/gds';
import {
  AppPermissionScope,
  AuthRole,
  UpdateProjectPermissionPayload,
  UpdateProjectPermissionInput,
  UpdateProjectPermissionError,
  AppModule,
  RestoreDefaultPermissionPayload,
  RestoreDefaultPermissionError,
  Scalars,
} from '@bechtel/types';
import { filter, get, find, includes, toUpper, map, forEach, findIndex } from 'lodash';
import { useRoleQuery } from '../hooks/use-role';
import { ApolloError, NetworkStatus, useLazyQuery, useQuery, useMutation } from '@apollo/client';
import PermissionActions from './actions';
import { PermissionMatrix } from './matrix';
import {
  GET_PROJECT_MODULES_PERMISSIONS,
  GET_ROLES_BY_IDS,
  RESTORE_DEFAULT_PERMISSIONS,
  UPDATE_PERMISSIONS,
} from '@bechtel/client-gql';
import { RolePermissionScope, ModuleCollapsed } from './types';
import { useAppUser, SuspenseLoader, ErrorRetry, ErrorBoundary } from '@bechtel/client-common';
import { nonePermissionScope, useDefaultProjectRoles } from './form-data';
import { PermissionMenuType } from './actions/types';
import { createSuccessToast } from './toast';
import RolePermissionUpdateError from './error';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

const Item = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
      {i18n._(item.name)}
    </Text>
  );
};

export const SelectedItem = ({ selectedItemCount, totalItemCount }: any) => {
  return (
    <Box pl={{ sm: '500' }}>
      <Text size="300" family="graphik" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
        {`${t`Viewing ${selectedItemCount} of ${totalItemCount} Roles`}`}
      </Text>
    </Box>
  );
};

function getInitialValues(moduleList: AppModule[], roleList: AuthRole[]): RolePermissionScope[] {
  const rolePermissionScopeList: RolePermissionScope[] = [];
  forEach(roleList, (roleItem) => {
    forEach(moduleList, (moduleItem) => {
      forEach(moduleItem.permissions, (permissionItem) => {
        const authPermisisonScope = find(roleItem.permissions, (rolePermissionItem) => {
          return (
            rolePermissionItem.permission.module.id === moduleItem.id &&
            rolePermissionItem.permission.id === permissionItem.id
          );
        });
        rolePermissionScopeList.push({
          id: authPermisisonScope
            ? authPermisisonScope.id
            : Math.random()
                .toString(36)
                .substr(2, 16),
          roleId: roleItem.id,
          moduleId: moduleItem.id,
          moduleTypeId: moduleItem.moduleTypeId,
          permissionId: permissionItem.id,
          permissionTypeId: permissionItem.permissionId,
          scope: authPermisisonScope ? authPermisisonScope.scope : nonePermissionScope,
        });
      });
    });
  });
  return rolePermissionScopeList;
}

export const ManagePermissions = () => {
  const { user } = useAppUser();
  const [selectedRoles, setSelectedRoles] = useState<AuthRole[]>([]);
  const [showRoleError, setShowRoleError] = useState<boolean>(false);
  const [updatePermissionError, setUpdatePermissionError] = useState<
    RestoreDefaultPermissionError | UpdateProjectPermissionError | ApolloError | null
  >(null);
  const [rolePermissionScopeList, setRolePermissionScopeList] = useState<RolePermissionScope[]>([]);
  const [collapseAll, setCollapseAll] = useState<boolean>(false);
  const [collapseModules, setCollapseModules] = useState<ModuleCollapsed[]>([]);
  const dispatchToast = useToastsDispatch();
  const [filteredModuleList, setFilteredModuleList] = useState<AppModule[]>([]);
  const defaultProjectRoles = useDefaultProjectRoles();
  const {
    loading: roleLoading,
    error: roleError,
    data: roleData,
    refetch: roleRefetch,
    networkStatus: roleNetworkStatus,
  } = useRoleQuery();
  const {
    loading: moduleLoading,
    data: moduleData,
    error: moduleError,
    refetch: moduleRefetch,
    networkStatus: moduleNetworkStatus,
  } = useQuery<{
    modules: AppModule[];
  }>(GET_PROJECT_MODULES_PERMISSIONS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      id: user?.project?.id,
    },
  });

  const [
    getRolesByIds,
    {
      loading: roleByIdsLoading,
      data: roleByIdsData,
      error: roleByIdsError,
      refetch: roleByIdsRefetch,
      networkStatus: roleByIdsNetworkStatus,
    },
  ] = useLazyQuery<{
    rolesByIds: AuthRole[];
  }>(GET_ROLES_BY_IDS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const roleList: AuthRole[] = get(roleData, 'roles', []);
  const moduleList = get(moduleData, 'projectConfigById.modules', []);
  const roleByIdsList = get(roleByIdsData, 'rolesByIds', []);
  const defaultValues = useRef<RolePermissionScope[]>([]);
  const dirty = () => JSON.stringify(defaultValues.current) !== JSON.stringify(rolePermissionScopeList);

  const [updatePermissions, { loading: mutationLoading }] = useMutation<
    { updateProjectPermissionPayload: UpdateProjectPermissionPayload },
    { inputs: UpdateProjectPermissionInput[] }
  >(UPDATE_PERMISSIONS, {
    onCompleted: (data) => {
      const updatedPermissionError = get(data, 'updateProjectPermissions.error');
      if (updatedPermissionError) {
        setUpdatePermissionError(updatedPermissionError);
      } else {
        setUpdatePermissionError(null);
        dispatchToast(createSuccessToast());
      }
    },
    onError: (error) => {
      setUpdatePermissionError(error);
    },
  });

  const [restoreDefaultPermissions, { loading: restoreLoading }] = useMutation<
    { restoreDefaultPermissionPayload: RestoreDefaultPermissionPayload },
    { inputs: Scalars['ID'][] }
  >(RESTORE_DEFAULT_PERMISSIONS, {
    onCompleted: (data) => {
      const restorePermissionError = get(data, 'restoreDefaultPermissions.error');
      if (restorePermissionError) {
        setUpdatePermissionError(restorePermissionError);
      } else {
        const defaultRoles = filter(roleList, (item: AuthRole) => {
          return includes(defaultProjectRoles, toUpper(item.name));
        });
        setSelectedRoles(defaultRoles);
        setUpdatePermissionError(null);
        dispatchToast(createSuccessToast(true));
      }
    },
    onError: (error) => {
      setUpdatePermissionError(error);
    },
  });

  useEffect(() => {
    if (moduleList.length === 0) return;
    setFilteredModuleList(filter(moduleList, (module) => module.permissions && module.permissions.length > 0));
  }, [moduleList]);

  useEffect(() => {
    if (filteredModuleList.length === 0) return;
    const collapsedList: ModuleCollapsed[] = filteredModuleList.map(({ id }) => ({
      moduleId: id,
      collapsed: false,
    }));
    setCollapseModules(collapsedList);
  }, [filteredModuleList]);

  useEffect(() => {
    const defaultRoles = filter(roleList, (item: AuthRole) => {
      return includes(defaultProjectRoles, toUpper(item.name));
    });
    setSelectedRoles(defaultRoles);
  }, [roleList]);

  useEffect(() => {
    if (selectedRoles.length === 0) return;
    getRolesByIds({
      variables: {
        roleIds: map(selectedRoles, 'id'),
      },
    });
  }, [selectedRoles, getRolesByIds]);

  useEffect(() => {
    if (filteredModuleList.length === 0 || roleByIdsList.length === 0) return;
    defaultValues.current = getInitialValues(filteredModuleList, roleByIdsList);
    setRolePermissionScopeList(defaultValues.current);
  }, [filteredModuleList, roleByIdsList]);

  const onExpandCollapseModule = (moduleId: string) => {
    setCollapseModules((prevState) => {
      const collapsedList = [...prevState];
      const selected = findIndex(collapsedList, (item) => {
        return item.moduleId === moduleId;
      });
      if (selected !== -1) {
        collapsedList[selected] = {
          ...collapsedList[selected],
          collapsed: !collapsedList[selected].collapsed,
        };
      }
      return collapsedList;
    });
  };

  const onScopeSelect = (
    roleId: UpdateProjectPermissionInput['projectRoleId'],
    permissionScopeId: UpdateProjectPermissionInput['id'],
    selectedScope: AppPermissionScope
  ) => {
    setRolePermissionScopeList((prevState) => {
      const roleModulePermissionScopes = [...prevState];
      const selected = findIndex(roleModulePermissionScopes, (item) => {
        return item.roleId === roleId && item.id === permissionScopeId;
      });
      if (selected !== -1 && roleModulePermissionScopes[selected].scope.id !== selectedScope.id) {
        roleModulePermissionScopes[selected] = {
          ...roleModulePermissionScopes[selected],
          scope: selectedScope,
        };
      }
      return roleModulePermissionScopes;
    });
  };

  const onMenuSelect = (menuType: PermissionMenuType) => {
    if (menuType === PermissionMenuType.EXPAND_COLLAPSE_ALL) {
      setCollapseAll(!collapseAll);
      setCollapseModules((prevState) => {
        const collapsedModules = [...prevState];
        const collapsedList = collapsedModules.map(({ moduleId }) => ({
          moduleId: moduleId,
          collapsed: !collapseAll,
        }));
        return collapsedList;
      });
    } else {
      const defaultRoles = filter(roleList, (item: AuthRole) => {
        return includes(defaultProjectRoles, toUpper(item.name));
      });
      if (defaultRoles.length > 0) {
        const inputs: Scalars['ID'][] = map(defaultRoles, 'id');
        restoreDefaultPermissions({
          variables: { inputs },
        });
      }
    }
  };

  const OnSaveChanges = async () => {
    const modifiedItems = rolePermissionScopeList.filter((roleItem) => {
      return defaultValues.current.some((defaultItem) => {
        return (
          defaultItem.roleId === roleItem.roleId &&
          defaultItem.id === roleItem.id &&
          defaultItem.scope.id !== roleItem.scope.id
        );
      });
    });

    const inputs: UpdateProjectPermissionInput[] = modifiedItems.map(
      ({ id, roleId, permissionTypeId, scope, moduleTypeId }) => ({
        id,
        projectRoleId: roleId,
        permissionId: permissionTypeId,
        scopeId: scope.id,
        moduleTypeId,
      })
    );
    await updatePermissions({
      variables: { inputs },
    });
  };

  return (
    <Fragment>
      <PermissionContainerStyled>
        <Box p={{ sm: '450' }} justifyContent={{ sm: 'flex-end' }}>
          <Box>
            {(restoreLoading || roleByIdsLoading || roleByIdsNetworkStatus === NetworkStatus.refetch) && (
              <Box mr={{ sm: '400' }} alignItems={{ sm: 'center' }} data-testid="rolebyids-loading">
                <Spinner size={SpinnerSize.MEDIUM} />
              </Box>
            )}
            {!roleLoading && roleError && (
              <ErrorRetry refetch={roleRefetch}>
                <Trans>There was a problem loading role list. Please click the button below to try again.</Trans>
              </ErrorRetry>
            )}
            {!roleError && (
              <Box style={{ width: '208px' }} flexDirection={{ sm: 'column' }} mr={{ sm: '400' }}>
                <MultiSelectCheckbox
                  items={roleList}
                  itemComponent={Item}
                  selectedComponent={SelectedItem}
                  initialSelectedItems={[...selectedRoles]}
                  size={TextFieldSize.MEDIUM}
                  showSelectAll={true}
                  confirmSelection={true}
                  appearance={TextFieldAppearance.NORMAL}
                  displayKeyField="name"
                  loading={roleLoading || roleNetworkStatus === NetworkStatus.refetch}
                  onSelectedItemsChange={async (selectedItems) => {
                    if (!selectedItems || selectedItems.length === 0) setShowRoleError(true);
                    else setShowRoleError(false);
                    selectedItems && setSelectedRoles(selectedItems);
                  }}
                ></MultiSelectCheckbox>
                {showRoleError && (
                  <Box mt={{ sm: '300' }}>
                    <Text color="bechtel.red" size="400">
                      <Trans>Please select one or more roles.</Trans>
                    </Text>
                  </Box>
                )}
              </Box>
            )}
            <PermissionActions onMenuSelect={onMenuSelect} />
          </Box>
        </Box>
        {selectedRoles.length > 0 && (
          <Fragment>
            {!roleByIdsError && !moduleError && (
              <Fragment>
                <PermissionMatrix
                  moduleList={filteredModuleList}
                  onScopeSelect={onScopeSelect}
                  rolePermissionScopeList={rolePermissionScopeList}
                  selectedRoles={selectedRoles}
                  collapseModules={collapseModules}
                  onExpandCollapseModule={onExpandCollapseModule}
                />
                <FreezeCellStyled />
              </Fragment>
            )}
            {(moduleLoading || moduleNetworkStatus === NetworkStatus.refetch) && (
              <Box
                flexGrow={{ sm: 1 }}
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                p={{ sm: '500' }}
                data-testid="module-loading"
              >
                <Spinner size={SpinnerSize.LARGE} />
              </Box>
            )}
            {!moduleLoading && moduleError && (
              <div data-testid="module-retry">
                <ErrorRetry refetch={moduleRefetch}>
                  <Trans>There was a problem loading modules. Please click the button below to try again.</Trans>
                </ErrorRetry>
              </div>
            )}
            {!roleByIdsLoading && roleByIdsError && (
              <div data-testid="roles-by-ids-retry">
                <ErrorRetry refetch={roleByIdsRefetch}>
                  <Trans>
                    There was a problem loading role permissions. Please click the button below to try again.
                  </Trans>
                </ErrorRetry>
              </div>
            )}
          </Fragment>
        )}
      </PermissionContainerStyled>
      {selectedRoles.length > 0 && !roleByIdsError && !moduleError && (
        <PermissionFooterStyled>
          <Box>
            <Box mr={{ sm: '400' }}>
              <FooterButtonStyled
                disabled={!dirty() || mutationLoading || restoreLoading}
                appearance={ButtonAppearance.SECONDARY}
                onClick={() => {
                  setRolePermissionScopeList(defaultValues.current);
                }}
              >
                <Trans>Discard Changes</Trans>
              </FooterButtonStyled>
            </Box>
            <FooterButtonStyled
              disabled={!dirty() || mutationLoading || restoreLoading}
              appearance={ButtonAppearance.SECONDARY}
              onClick={OnSaveChanges}
            >
              {mutationLoading && <Spinner />}
              {!mutationLoading && t`Save Changes`}
            </FooterButtonStyled>
          </Box>
        </PermissionFooterStyled>
      )}
      {updatePermissionError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <RolePermissionUpdateError
              updatePermissionError={updatePermissionError}
              close={() => {
                setUpdatePermissionError(null);
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};
